import { formatDateToYYYYMMDD } from "../../../../../helpers/functions";

const REFERRAL_NOTIFICATION_COMPARE_FIELDS = [
  "referralType",
  "clinicName",
  "firstName",
  "lastName",
  "middleName",
  "licenseNumber",
  "designation",
  "isPrimaryPhysician",
  "phoneNumber",
  "fax",
  "emailAddress",
  "address",
  "unitNumber",
  "poBox",
  "city",
  "province",
  "postalCode",
  "country",
];

export const areReferralAndNotificationInfosSame = (referral, notification) => {
  if (!referral || !notification) return false;

  return REFERRAL_NOTIFICATION_COMPARE_FIELDS.every(
    (field) => (referral?.[field] ?? "") === (notification?.[field] ?? "")
  );
};

export function buildPrimaryProviderPayload(provider) {
  return {
    referralId: null,
    referralType: "Primary Provider",
    clinicName: "",
    firstName: provider.firstName,
    lastName: provider.lastName,
    middleName: provider?.middleName,
    licenseNumber: provider?.licenseNumber,
    designation: provider?.title,
    isPrimaryPhysician: provider?.primaryProvider,
    phoneNumber: provider?.contactInfo?.primaryPhone,
    fax: provider?.contactInfo?.fax,
    emailAddress: provider?.contactInfo?.email,
    address: provider?.contactInfo?.address,
    unitNumber: provider?.contactInfo?.unitNumber,
    poBox: provider?.contactInfo?.poBox,
    city: provider?.contactInfo?.city,
    province: provider?.contactInfo?.province,
    postalCode: provider?.contactInfo?.postalCode,
    country: provider?.contactInfo?.country,
    comments: provider?.contactInfo?.comments,
  };
}

export function buildNotificationDocumentationFields({
  info,
  newType,
  selectedTypeOverride = null,
  docFieldOverrides = null,
  notificationDate,
  methodOfNotification,
}) {
  const obj = info !== null ? { ...info, comments: "notification" } : null;
  let updatedNotifications = [];
  let updatedNotificationInfo = [];

  if (info !== null) {
    updatedNotifications = [selectedTypeOverride ?? obj.referralType];
    updatedNotificationInfo = [obj];
  } else {
    updatedNotifications = [newType];
    updatedNotificationInfo = [];
  }

  const resolvedDate =
    docFieldOverrides?.dateOfNotification !== undefined
      ? docFieldOverrides.dateOfNotification
      : notificationDate;
  const resolvedMethod =
    docFieldOverrides?.methodOfNotification !== undefined
      ? docFieldOverrides.methodOfNotification
      : methodOfNotification;

  return {
    notificationType: updatedNotifications,
    notificationInformations: updatedNotificationInfo,
    dateOfNotification: resolvedDate,
    methodOfNotification: resolvedMethod,
  };
}

export function getMinorAilmentCombinedCardDerivedState({
  documentation,
  shouldShowReferral,
  referralType,
  notificationType,
}) {
  const referralInformation = documentation?.referralInformations?.[0] ?? null;
  const notificationInformation =
    documentation?.notificationInformations?.[0] ?? null;
  const combinedCardInformation =
    notificationType === "same" ? referralInformation : notificationInformation;

  const showCombinedProviderInfoCard = Boolean(
    shouldShowReferral &&
      referralType &&
      referralType !== "No Referral" &&
      ((notificationType === "same" && !!referralInformation) ||
        (notificationType === referralType &&
          notificationType !== "nonApplicable" &&
          areReferralAndNotificationInfosSame(
            referralInformation,
            notificationInformation
          ))) &&
      !!combinedCardInformation &&
      (combinedCardInformation?.referralType === referralType ||
        notificationType === "same") &&
      notificationType !== "nonApplicable" &&
      referralType !== "No Referral" &&
      notificationType !== null
  );

  return {
    referralInformation,
    notificationInformation,
    combinedCardInformation,
    showCombinedProviderInfoCard,
  };
}

export function buildReferralNotificationCardGroups({
  referralInformations = [],
  notificationInformations = [],
  isSameAsReferralSelected = false,
}) {
  const matchedReferralIndexes = new Set();
  const matchedNotificationIndexes = new Set();
  const combinedInfoCards = [];

  if (isSameAsReferralSelected) {
    referralInformations.forEach((ref, idx) => {
      combinedInfoCards.push({
        key: `same-ref-${ref?.referralId ?? idx}`,
        info: ref,
        source: "same",
      });
      matchedReferralIndexes.add(idx);
    });
  } else {
    referralInformations.forEach((ref, refIndex) => {
      const matchingNotificationIndex = notificationInformations.findIndex(
        (not, notIndex) =>
          !matchedNotificationIndexes.has(notIndex) &&
          ref?.referralType === not?.referralType &&
          areReferralAndNotificationInfosSame(ref, not)
      );

      if (matchingNotificationIndex >= 0) {
        matchedReferralIndexes.add(refIndex);
        matchedNotificationIndexes.add(matchingNotificationIndex);
        combinedInfoCards.push({
          key: `matched-${notificationInformations[matchingNotificationIndex]?.referralId ?? matchingNotificationIndex}`,
          info: notificationInformations[matchingNotificationIndex],
          source: "matched",
        });
      }
    });
  }

  const standaloneReferralInfos = referralInformations.filter(
    (_, idx) => !matchedReferralIndexes.has(idx)
  );
  const standaloneNotificationInfos = isSameAsReferralSelected
    ? []
    : notificationInformations.filter((not, idx) => {
        if (matchedNotificationIndexes.has(idx)) return false;

        const alreadyShownInCombinedCard = combinedInfoCards.some(
          (card) =>
            card?.info?.referralType === not?.referralType &&
            areReferralAndNotificationInfosSame(card?.info, not)
        );

        return !alreadyShownInCombinedCard;
      });

  return {
    combinedInfoCards,
    standaloneReferralInfos,
    standaloneNotificationInfos,
  };
}

export function getNotificationCopyDocOverrides(documentation, currentMethodState) {
  return {
    dateOfNotification: formatDateToYYYYMMDD(new Date()),
    methodOfNotification:
      documentation?.methodOfNotification || currentMethodState || "fax",
  };
}
